/**
 * IBSService entity adapter (P15.2 / #416).
 *
 * The bank's Important Business Services -- driven by SAMA outcome
 * categories. Carries MVB + sovereign flags that the resilience +
 * blast derivers read.
 */
import { NoOwnerFlag } from "shared/components/NoOwnerFlag";
import { i18n } from "shared/i18n";
import {
  IBSChannelLinks,
  IBSFundedByLinks,
  IBSFundingResourceLinks,
  IBSRegulatoryLinks,
} from "./ibsLinks";
import { ImpactToleranceDeclarationsSection } from "./impactToleranceDeclarations";
import type { AdminColumn, EntityAdapter, FormField } from "./types";
import { outcomeCategoryLabel } from "shared/vocab/workflowState";

export type OutcomeCategory =
  | "minimize_customer_harm"
  | "retain_customer_confidence"
  | "regulatory_engagement"
  | "protect_safety_and_soundness";

export interface IBSService {
  id: string;
  code: string;
  name: string;
  description: string | null;
  outcome_category: OutcomeCategory;
  mvb_flag: boolean;
  // Derived from service_class since Alembic 0213; read-only.
  customer_facing: boolean;
  // P429.1: the register class; a Critical Internal Function is "internal".
  service_class?: "customer" | "state" | "internal";
  // P368: recovery horizon (null = derived from the tolerance).
  mvb_horizon: string | null;
  sovereign_flag: boolean;
  // P136 (P117f.4): implied-SSOT scale & scope fields.
  customer_volume: number | null;
  peak_tps: number | null;
  regulatory_refs: string | null;
  channels: string | null;
  owner_name: string | null;
  owner_email: string | null;
  created_at: string;
  updated_at: string;
  admission_status?: string | null;
}

export interface IBSServiceIn {
  code: string;
  name: string;
  description: string | null;
  outcome_category: OutcomeCategory;
  mvb_flag: boolean;
  service_class?: "customer" | "state" | "internal";
  mvb_horizon?: string | null;
  sovereign_flag: boolean;
  customer_volume?: number | null;
  peak_tps?: number | null;
  regulatory_refs?: string | null;
  channels?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
}

export type IBSServicePatch = Partial<IBSServiceIn>;

const MVB_HORIZON_OPTIONS = [
  { value: "t_plus_4h", label: "T+4h" },
  { value: "t_plus_24h", label: "T+24h" },
  { value: "t_plus_72h", label: "T+72h" },
  { value: "t_plus_1w", label: "T+1 week" },
];

// P429.6g: the class replaces the customer-facing checkbox. A State
// service or a Critical Internal Function owes no delivery channel; a
// service joins the internal class through the CIF register, which is
// why the API refuses that move from this drawer.
const SERVICE_CLASS_OPTIONS = [
  {
    value: "customer",
    label: "Customer service (reaches customers through a channel)",
  },
  { value: "state", label: "State service (indirect; no delivery channel)" },
  {
    value: "internal",
    label: "Critical Internal Function (set on the CIF register)",
  },
];

const OUTCOME_OPTIONS = [
  { value: "minimize_customer_harm", label: "Minimise customer harm" },
  { value: "retain_customer_confidence", label: "Retain customer confidence" },
  { value: "regulatory_engagement", label: "Regulatory engagement" },
  { value: "protect_safety_and_soundness", label: "Safety + soundness" },
];

/* Round 9 E8 -- this map moved to shared/vocab, because `twin` cannot
   import `foundation` and the IBS admission register spent four rounds
   printing the raw column value for want of it. The shorter forms this
   held ("Safety+soundness") were for a narrow admin column; the shared
   ones read as English and the column is wide enough. */

const columns: AdminColumn<IBSService>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => (
      <span className="font-medium text-slate-900">
        {r.name}
        {/* Round 20 V5 -- admitted, and nobody recorded as its owner. */}
        {r.admission_status === "admitted" &&
          !(r.owner_name ?? "").trim() &&
          !(r.owner_email ?? "").trim() && (
            <NoOwnerFlag testid="ibs-register-no-owner" />
          )}
      </span>
    ),
  },
  {
    key: "outcome_category",
    label: "Outcome",
    sortValue: (r) => r.outcome_category,
    render: (r) => (
      <span
        data-testid="ibs-outcome-chip"
        className="inline-flex items-center rounded-full bg-slate-100 text-slate-800 px-2 py-0.5 text-xs"
      >
        {outcomeCategoryLabel(r.outcome_category)}
      </span>
    ),
  },
  {
    key: "mvb_flag",
    label: "MVB",
    sortValue: (r) => (r.mvb_flag ? 1 : 0),
    render: (r) =>
      r.mvb_flag ? (
        <span
          data-testid="ibs-mvb-chip"
          className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-900 px-2 py-0.5 text-xs font-medium"
        >
          {i18n.t("screens.ibsAdapter.mvb")}
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "sovereign_flag",
    label: "Sovereign",
    sortValue: (r) => (r.sovereign_flag ? 1 : 0),
    render: (r) =>
      r.sovereign_flag ? (
        <span
          data-testid="ibs-sovereign-chip"
          className="inline-flex items-center rounded-full bg-blue-100 text-blue-900 px-2 py-0.5 text-xs font-medium"
        >
          {i18n.t("screens.ibsAdapter.sovereign")}
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<IBSServiceIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  {
    kind: "select",
    key: "outcome_category",
    label: "Outcome category",
    required: true,
    options: OUTCOME_OPTIONS,
  },
  { kind: "bool", key: "mvb_flag", label: "MVB-flagged" },
  {
    kind: "select",
    key: "service_class",
    label: "Register class",
    required: true,
    options: SERVICE_CLASS_OPTIONS,
  },
  {
    kind: "select",
    key: "mvb_horizon",
    label: "MVB recovery horizon",
    options: MVB_HORIZON_OPTIONS,
  },
  { kind: "bool", key: "sovereign_flag", label: "Sovereign-mandated" },
  // Implied-SSOT scale & scope (P136 / P117f.4).
  {
    kind: "number",
    key: "customer_volume",
    label: "Customer volume",
    min: 0,
    group: "Scale & scope",
  },
  {
    kind: "number",
    key: "peak_tps",
    label: "Peak TPS",
    min: 0,
    group: "Scale & scope",
  },
  {
    kind: "text",
    key: "regulatory_refs",
    label: "Regulatory refs",
    group: "Scale & scope",
  },
  { kind: "text", key: "channels", label: "Channels", group: "Scale & scope" },
  // E8 -- nothing in the product could set an owner, so putting a
  // Service Owner on his own work meant editing the database.
  {
    kind: "text",
    key: "owner_name",
    label: "Owner",
    group: "Accountability",
  },
  {
    kind: "text",
    key: "owner_email",
    label: "Owner email (the address they sign in with)",
    group: "Accountability",
  },
];

export const ibsAdapter: EntityAdapter<IBSService, IBSServiceIn, IBSServicePatch> = {
  i18nPrefix: "modules.foundation.admin.ibses",
  importKind: "ibs_service",
  endpoint: "/v1/admin/foundation/ibses",
  queryKey: ["foundation-admin", "ibses"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    outcome_category: "minimize_customer_harm",
    mvb_flag: false,
    service_class: "customer",
    mvb_horizon: null,
    sovereign_flag: false,
    customer_volume: null,
    peak_tps: null,
    regulatory_refs: null,
    channels: null,
    owner_name: null,
    owner_email: null,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    outcome_category: r.outcome_category,
    mvb_flag: r.mvb_flag,
    service_class: r.service_class ?? "customer",
    mvb_horizon: r.mvb_horizon,
    sovereign_flag: r.sovereign_flag,
    customer_volume: r.customer_volume,
    peak_tps: r.peak_tps,
    regulatory_refs: r.regulatory_refs,
    channels: r.channels,
    owner_name: r.owner_name,
    owner_email: r.owner_email,
  }),
  extraSections: [
    IBSChannelLinks,
    IBSFundingResourceLinks,
    IBSFundedByLinks,
    IBSRegulatoryLinks,
    ImpactToleranceDeclarationsSection,
  ],
};
